<script setup lang="ts">
import {computed, nextTick, type PropType, ref, toRaw} from 'vue';
import {USER_CHANGE_EMIT, USER_REMOVE_EMIT, USER_SESSIONS_ENDED_EMIT} from '@/base/cabConstants';
import {CabOrganization, CabTheme, type CabUser, Role, type SessieTelling, uuidOf} from 'common';
import DeleteButton from '@/views/icons/DeleteIcon.vue';
import InfoIcon from '@/views/icons/InfoIcon.vue';
import UserAuthDialog from '@/views/admin/UserAuthDialog.vue';
import {CascaderOption, ElCascader, ElMessage} from 'element-plus';
import {roleLabel} from '@/base/utils';

const emits = defineEmits([USER_REMOVE_EMIT, USER_CHANGE_EMIT, USER_SESSIONS_ENDED_EMIT]);
const props = defineProps({
    user: {
        type    : Object as PropType<CabUser>,
        required: true,
    },
    allOrganizations: {
        type    : Object as PropType<CabOrganization[]>,
        required: true,
    },
    selectableRoles: {
        type    : Object as PropType<Role[]>,
        required: false,
        default : Object.values(Role),
    },
    me: {
        type    : Object as PropType<CabUser>,
        required: true,
    },
    orgThemeOptions: {
        type    : Object as PropType<CascaderOption[]>,
        required: true,
    },
    allUsers: {
        type    : Object as PropType<CabUser[]>,
        required: true,
    },
    telling: {
        type    : Object as PropType<SessieTelling>,
        required: false,
    },
});
defineExpose({
    firstEdit,
});

const user             = ref<CabUser>(props.user);
const nameField        = ref<HTMLInputElement | null>(null);
const nameEditing      = ref<boolean>(false);
const emailField       = ref<HTMLInputElement | null>(null);
const emailEditing     = ref<boolean>(false);
const authDialog       = ref<boolean>(false);
const orgThemeSelected = ref(props.user?.getOrgThemeCombiUuids());
const roleOptions      = computed<{ label: string; value: string }[]>(() =>
    props.selectableRoles.map((role: Role) => ({
        label: roleLabel(role),
        value: role,
    })),
);

const sessieTitle = computed<string>(() => {
    if (!props.telling || (props.telling.sessies === 0 && props.telling.browsers === 0)) {
        return 'geen actieve sessies';
    }
    return `${metAantal(props.telling.sessies, 'sessie', 'sessies')}, ${metAantal(props.telling.browsers, 'vertrouwde browser', 'vertrouwde browsers')}`;
});

const isEmailDuplicate = computed<boolean>(() => {
    if (!user.value.email || !user.value.email.trim()) {
        return false;
    }
    const currentEmail = user.value.email.toLowerCase().trim();
    const duplicateCount = props.allUsers.filter(u => 
        u.email && u.email.toLowerCase().trim() === currentEmail
    ).length;
    return duplicateCount > 1;
});
let isFirstEdit: boolean = false;

//==========================================
function metAantal(n: number, enkelvoud: string, meervoud: string): string {
    return `${n} ${n === 1 ? enkelvoud : meervoud}`;
}

//==========================================
function firstEdit() {
    isFirstEdit = true;
    startEditingName();
}

//==========================================
function startEditingName() {
    nameEditing.value = true;
    nextTick(() => nameField.value!.focus());
}

function stopEditingName() {
    nameEditing.value = false;
    nameField.value!.blur();
    if (isFirstEdit) {
        if (!props.user.name.trim()) {
            onDelete();
        }
        isFirstEdit = false;
    }
}

//==========================================
function startEditingEmail() {
    emailEditing.value = true;
    nextTick(() => emailField.value!.focus());
}

function stopEditingEmail() {
    emailEditing.value = false;
    emailField.value!.blur();
}

//==========================================
function onDelete() {
    emits(USER_REMOVE_EMIT, user.value);
}

function onChangeOrgTheme() {
    const rawSelected: string[][] = toRaw(orgThemeSelected.value).map(path => toRaw(path));

    if (rawSelected.length === 0) {
        // Revert to previous selection
        orgThemeSelected.value = props.user.getOrgThemeCombiUuids();
        ElMessage.warning('Er moet minimaal één instelling/thema geselecteerd blijven.');
        return;
    }

    const newOrgs: CabOrganization[] = [];
    const newThemes: CabTheme[]      = [];
    rawSelected.forEach(([oUuid, tUuid]) => {
        const o = props.allOrganizations!.find((o) => uuidOf(o) === oUuid)!;
        const t = o.themes.find((t) => uuidOf(t) === tUuid)!;
        newOrgs.push(o);
        newThemes.push(t);
    });
    Object.assign(user.value, {
        organizations: newOrgs,
        themes       : newThemes,
    });
    onChange();
}

function onChange() {
    // Ensure roles is always a single-value array (if not empty)
    if (user.value.roles) {
        if (Array.isArray(user.value.roles)) {
            user.value.roles = user.value.roles.length > 0 ? [user.value.roles[0]] : [];
        } else if (typeof user.value.roles === 'string') {
            user.value.roles = [user.value.roles];
        }
    }
    emits(USER_CHANGE_EMIT, user.value);
}

function dynInputClassName(b: boolean) {
    return b ? `bg-whiteborder-gray-300 rounded-md shadow-xl` : `bg-yellow-100`;
}

function dynInputClassEmail(b: boolean) {
    //+ ' ' + (isEmailDuplicate ? 'bg-red-600 text-white' : '')
    return b ? `bg-whiteborder-gray-300 rounded-md shadow-xl ${isEmailDuplicate.value ? 'bg-red-600 text-white' : ''}`
        : `${isEmailDuplicate.value ? 'bg-red-600 text-white' : 'bg-yellow-100'}`;
}

//==========================================
function onSessionsEnded() {
    emits(USER_SESSIONS_ENDED_EMIT);
}

function onEmailChange() {
    user.value.email = user.value.email ? user.value.email.toLowerCase().trim() : '';
    onChange();
}
</script>

<template>
    <div
        class="flex flex-row items-center w-full font-medium m-0.5 p-1 text-gray-900 bg-yellow-100"
        data-testid="user-strip"
    >
        <div class="w-60 p-1" title="naam van de rapporteur">
            <input
                class="mt-1 mr-1 px-2 py-1 border-1 w-full"
                :class="dynInputClassName(nameEditing)"
                type="text"
                ref="nameField"
                v-model="user.name"
                @input="onChange"
                @click="startEditingName()"
                @focus="startEditingName()"
                @keyup.enter="stopEditingName()"
                @blur="stopEditingName()"
                placeholder="name"
                data-testid="user-name"
            />
        </div>

        <div class="w-60 p-1" title="email van de rapporteur">
            <input
                class="mt-1 mr-1 px-2 py-1 border-1 w-full"
                :class="dynInputClassEmail(emailEditing)"
                type="text"
                ref="emailField"
                v-model="user.email"
                @input="onEmailChange"
                @click="startEditingEmail()"
                @focus="startEditingEmail()"
                @keyup.enter="stopEditingEmail()"
                @blur="stopEditingEmail()"
                placeholder="email"
                data-testid="user-email"
            />
        </div>

        <div class="w-40 p-1" title="rol van de gebruiker">
            <el-cascader
                class="w-full"
                v-model="user.roles"
                :options="roleOptions"
                :disabled="roleOptions.length === 1"
                :props="{ checkStrictly: true, emitPath: false }"
                clearable
                placeholder="Selecteer rol"
                @change="onChange"
            />
        </div>

        <div class="flex-1" data-testid="thema-org-select">
            <el-cascader
                class="w-full"
                v-model="orgThemeSelected"
                :options="orgThemeOptions"
                :props="{ multiple: true }"
                clearable
                placeholder="Selecteer instelling/themas"
                @change="onChangeOrgTheme"
            />
        </div>

        <div class="w-12 text-center text-xs" title="inloggen met een code per mail" data-testid="two-factor-state">
            {{ user.tweeFactor ? '2FA' : '' }}
        </div>

        <div class="w-16 text-center text-xs" :title="sessieTitle" data-testid="session-count">
            {{ props.telling?.sessies || '' }}
        </div>

        <div class="icon-button-space">
            <InfoIcon
                class="cursor-pointer"
                title="2FA, laatste login, wachtwoordmail en sessies"
                data-testid="user-auth-button"
                @click="authDialog = true"
            />
        </div>

        <DeleteButton @click="onDelete()"/>
    </div>

    <UserAuthDialog
        v-model="authDialog"
        :user="user"
        :telling="props.telling"
        v-on:[USER_CHANGE_EMIT]="onChange"
        v-on:[USER_SESSIONS_ENDED_EMIT]="onSessionsEnded"
    />
</template>
